import { type OrderBy } from '@inpart-io/devtools-iap';
import { type BoardLayout } from '@inpart-io/padme-iap';
import { computed, type ComputedRef, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';

import { ADD_ITEMS, type DataObjectIdentity, REFRESH_PAGE, REMOVE_ITEMS, SET_LAYOUT, SET_ORDER_BY, SET_PAGE_SIZE, SET_VISIBLE_COLUMNS } from '../store';
import { PAGINATION_LOADING_DELAY, PAGINATION_PREFIX } from './pagination.constants';

/**
 * An interface representing methods to interact with pagination state and a computed property representing the module key.
 */
interface PaginationStoredLocator {
    /**
     * Computed property representing the module key.
     */
    moduleKey: ComputedRef<string>;
    persistenceBaseKey: ComputedRef<string>;
    /**
     * Computed property representing the total from the pagination state.
     */
    total?: ComputedRef<number>;
    /**
     * Computed property for the total number of accessible results, without filtering.
     */
    unfilteredCount?: ComputedRef<number>;
    /**
     * Method to add items to the pagination state
     * @param {DataObjectIdentity[]} toAdd list of ids to add
     * @returns
     */
    addItems?: (toAdd: DataObjectIdentity[]) => void;
    /**
     * Method to remove items from the pagination state
     * @param {DataObjectIdentity[]} toRemove list of ids to remove
     */
    removeItems?: (toRemove: DataObjectIdentity[]) => void;
    /**
     * Method to refresh the current page
     */
    refreshPage?: () => void;
    /**
     * Method to refresh the current page after a specified delay.
     * @param {number} [delay] - Optional delay in milliseconds before refreshing the page.
     */
    delayedRefreshPage?: (delay?: number) => void;
    /**
     * Method to set the visible columns in a particular list
     * @param {string[]} Name of the columns to be displayed
     */
    setVisibleColumns?: (visibleColumns: string[]) => void;
    /**
     *
     * Method to set the number of element per page
     * @param {number} The number of element to be displayed
     */
    setPageSize?: (pageSize: number) => void;
    /**
     *
     * Method to set order by statements in the current query
     * @param {OrderBy[]}
     */
    setOrderBy?: (orderBy: OrderBy[]) => void;
    /**
     *
     * Method to set the type of layout
     * @param {BoardLayout}
     */
    setLayout?: (layout: BoardLayout) => void;
}

/**
 * Represents optional parameters for the `usePaginationStoredLocator` function.
 */
export interface PaginationStoredLocatorParams {
    /**
     * Specifies a suffix to differentiate pagination states for different scenarios.
     */
    suffix?: string;
    /**
     * Indicates whether to include actions from the store in the returned object.
     */
    getActions?: boolean;
    /**
     * Indicates whether to include getters from the store in the returned object.
     */
    getGetters?: boolean;
}

/**
 * Custom hook to manage pagination state and actions for a specific dataObjectType within a Vuex store.
 * @param {string} dataObjectType - The type of data object for which pagination is managed.
 * @param {PaginationStoredLocatorParams} [params] - An optional parameters to differentiate pagination states for different scenarios.
 * @returns {PaginationStoredLocator} - An object containing reactive properties and methods to interact with pagination state.
 */
export function usePaginationStoredLocator(dataObjectType: string, params?: PaginationStoredLocatorParams): PaginationStoredLocator {
    const { suffix, getGetters = false, getActions = false } = params ?? {};

    // Private method to calculate the Vuex Module Key
    const getModuleKey = (): string => {
        return suffix ? `${PAGINATION_PREFIX}${route.path}_${dataObjectType}_${suffix}` : `${PAGINATION_PREFIX}${route.path}_${dataObjectType}`;
    };

    // Access the current route
    const route = useRoute();
    // Access the Vuex store at the current context
    const store = useStore();

    // Data
    const _moduleKey = ref<string>(getModuleKey());

    // Computed property to generate a unique key for the pagination module
    const moduleKey = computed(() => {
        return _moduleKey.value?.replaceAll('/', '_') ?? '';
    });

    const persistenceBaseKey = computed(() => {
        return suffix ? `${route.name as string}_${dataObjectType}_${suffix}` : `${route.name as string}_${dataObjectType}`;
    });

    let standardReturn: PaginationStoredLocator = { moduleKey, persistenceBaseKey };

    // Watch for changes in the Route to recalculate the Module Key. This only applies if the route name did not changed (same page but different context)
    watch(
        () => {
            return route.name;
        },
        (value, oldValue) => {
            if (value === oldValue) {
                _moduleKey.value = getModuleKey();
            }
        }
    );
    watch(
        () => {
            return route.params.id;
        },
        (value, oldValue) => {
            if (value && value !== oldValue) {
                _moduleKey.value = getModuleKey();
            }
        }
    );
    watch(route, (value, oldValue) => {
        if (value.name === oldValue.name && value.path !== oldValue.path) {
            _moduleKey.value = getModuleKey();
        }
    });

    if (getGetters) {
        const total = computed<number>(() => {
            // Reading directly the state from the store, as getters can have problems if the module doesn't exist yet.
            return store.state[moduleKey.value]?.total ?? 0;
        });
        const unfilteredCount = computed<number>(() => {
            // Reading directly the state from the store, as getters can have problems if the module doesn't exist yet.
            return store.state[moduleKey.value]?.unfilteredCount ?? 0;
        });
        standardReturn = { ...standardReturn, total, unfilteredCount };
    }

    if (getActions) {
        const addItems = (toAdd: DataObjectIdentity[]): void => {
            void store.dispatch(`${moduleKey.value}/${ADD_ITEMS}`, { toAdd });
        };

        const removeItems = (toRemove: DataObjectIdentity[]): void => {
            void store.dispatch(`${moduleKey.value}/${REMOVE_ITEMS}`, { toRemove });
        };

        const refreshPage = (): void => {
            void store.dispatch(`${moduleKey.value}/${REFRESH_PAGE}`);
        };

        const delayedRefreshPage = (delay?: number): void => {
            setTimeout(() => {
                refreshPage();
            }, delay ?? PAGINATION_LOADING_DELAY);
        };

        const setVisibleColumns = (visibleColumns: string[]): void => {
            void store.dispatch(`${moduleKey.value}/${SET_VISIBLE_COLUMNS}`, {
                visibleColumns
            });
        };

        const setPageSize = (pageSize: number): void => {
            void store.dispatch(`${moduleKey.value}/${SET_PAGE_SIZE}`, {
                pageSize
            });
        };

        const setOrderBy = (orderBy: OrderBy[]): void => {
            void store.dispatch(`${moduleKey.value}/${SET_ORDER_BY}`, {
                orderBy
            });
        };

        const setLayout = (layout: BoardLayout): void => {
            void store.dispatch(`${moduleKey.value}/${SET_LAYOUT}`, {
                layout
            });
        };

        standardReturn = { ...standardReturn, addItems, removeItems, refreshPage, delayedRefreshPage, setVisibleColumns, setPageSize, setOrderBy, setLayout };
    }

    return standardReturn;
}
